import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface StatusMessageProps {
  status?: string;
}

const DEFAULT_STATUS_MESSAGE = 'Thinking...';

const StatusMessage = ({ status }: StatusMessageProps) => {
  const statusHistoryRef = useRef<string[]>([]);
  const currentIndexRef = useRef<number>(-1);
  const [currentStatusMessage, setCurrentStatusMessage] = useState<string>(
    DEFAULT_STATUS_MESSAGE
  );

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (status && status.trim()) {
      const lastStatus =
        statusHistoryRef.current[statusHistoryRef.current.length - 1];
      if (
        lastStatus !== status.trim() &&
        status.trim() !== DEFAULT_STATUS_MESSAGE
      ) {
        statusHistoryRef.current = [...statusHistoryRef.current, status.trim()];
      }
    }
  }, [status]);

  const updateStatusMessage = useCallback(() => {
    const currentHistory = statusHistoryRef.current;
    const currentIndex = currentIndexRef.current;

    if (currentHistory.length === 0) {
      return;
    }

    if (currentIndex < currentHistory.length - 1) {
      currentIndexRef.current += 1;
      setCurrentStatusMessage(currentHistory[currentIndexRef.current]);
    }
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      updateStatusMessage();
    }, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [updateStatusMessage]);

  // Clear status history when conversation ends
  useEffect(() => {
    if (!status || status.trim() === '') {
      const timer = setTimeout(() => {
        statusHistoryRef.current = [];
        currentIndexRef.current = -1;
        setCurrentStatusMessage('Thinking...');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  return (
    <div className="flex items-center space-x-1 h-5 min-w-0">
      <AnimatePresence mode="wait">
        <motion.span
          key={currentStatusMessage}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className="truncate text-sm italic opacity-70 bg-gradient-to-r from-gray-400 via-gray-300 to-gray-400 dark:from-gray-600 dark:via-gray-500 dark:to-gray-600 bg-[length:200%_100%] bg-clip-text text-transparent animate-shimmer"
        >
          {currentStatusMessage}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

export default StatusMessage;
